"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TICKET_ERROR_CODES = exports.PERSONAL_DATA_KEYS = exports.CODICE_FISCALE_PATTERN = exports.PRE_ACCESSO_FORBIDDEN_FIELDS = exports.createPreAccessoTicketSchema = exports.createTicketSchema = exports.TICKET_CODE_PATTERN = exports.IDEMPOTENCY_KEY_PATTERN = exports.IDEMPOTENT_REPLAYED_HEADER = exports.IDEMPOTENCY_KEY_HEADER = exports.TICKET_EVENT_TYPE_LABELS = exports.TICKET_EVENT_TYPES = exports.RECORD_REFERENCE_ID_MAX = exports.REPORTER_ID_MAX = exports.TICKET_TEXT_MAX = void 0;
exports.formatTicketCode = formatTicketCode;
exports.routeHasIdentifier = routeHasIdentifier;
exports.forbiddenPreAccessoFields = forbiddenPreAccessoFields;
exports.containsCodiceFiscale = containsCodiceFiscale;
exports.forbiddenPersonalDataFields = forbiddenPersonalDataFields;
exports.mentionsPaziente = mentionsPaziente;
const zod_1 = require("zod");
const consumers_js_1 = require("./consumers.js");
const inbound_mail_js_1 = require("./inbound-mail.js");
const product_config_js_1 = require("./product-config.js");
const reply_drafts_js_1 = require("./reply-drafts.js");
const ticket_assignment_js_1 = require("./ticket-assignment.js");
const ticket_closure_js_1 = require("./ticket-closure.js");
const ticket_emails_js_1 = require("./ticket-emails.js");
const ticket_transitions_js_1 = require("./ticket-transitions.js");
/**
 * Ricezione dei ticket dai consumer (PRD-003 US-3, story #7; contratto di integrazione §5, §9).
 * Le chiavi del payload sono in italiano e in camelCase, come il resto del contratto verso i
 * prodotti (`committente`, `segnalante`, `unitaOperativa`); gli identificatori del codice in inglese.
 */
/** Lunghezza massima del testo della segnalazione; la minima per prodotto la applica il consumer (#18). */
exports.TICKET_TEXT_MAX = 10_000;
/** Lunghezza massima dell'identificativo del segnalante, alla creazione e nella lettura dello stato (#73). */
exports.REPORTER_ID_MAX = 200;
/**
 * Lunghezza massima dell'identificativo del record nel riferimento al record (DEC-014), come
 * `identificativoPaziente`.
 */
exports.RECORD_REFERENCE_ID_MAX = 200;
/**
 * Tipi di evento del ticket: la ricezione è il primo (DEC-002); consegna, mancato recapito e
 * reclamo delle email arrivano dai webhook della posta (#37); ricezione, rifiuto (assenza
 * dichiarata) e cancellazione per retention degli allegati dei consumer da #19 (DEC-010); ogni
 * cambio di stato è `stato_cambiato` (DEC-002); assegnazioni e rimozioni con #4; la correzione dello
 * sviluppo con #6; il collegamento della posta non associata con #42; la conferma di ricezione a chi
 * ha segnalato con #117; gli altri con #11. L'ordine è quello degli `ADD VALUE` dell'enum nel
 * database (0007, 0008, 0009 del cambio di priorità #3, 0010, 0011, 0013, 0014, 0018).
 */
exports.TICKET_EVENT_TYPES = [
    'ricevuto',
    ...ticket_emails_js_1.TICKET_EMAIL_EVENT_TYPES,
    'allegato_ricevuto',
    'allegato_rifiutato',
    'allegato_eliminato',
    // Cambio di priorità dal triage (#3): `details` = `{ from, to, memberId }`.
    'priorita_cambiata',
    ...ticket_transitions_js_1.TICKET_STATE_EVENT_TYPES,
    ...ticket_assignment_js_1.TICKET_ASSIGNMENT_EVENT_TYPES,
    ...ticket_closure_js_1.TICKET_CLOSURE_EVENT_TYPES,
    // Posta in ingresso (#42): collegamento dalla posta non associata.
    ...inbound_mail_js_1.TICKET_INBOUND_EVENT_TYPES,
    // Conferma di ricezione a chi ha segnalato dal canale in piattaforma (#117).
    ...ticket_emails_js_1.TICKET_RECEIPT_EVENT_TYPES,
    // Bozza di risposta salvata via MCP (#158), senza il testo.
    ...reply_drafts_js_1.TICKET_REPLY_DRAFT_EVENT_TYPES,
];
exports.TICKET_EVENT_TYPE_LABELS = {
    ricevuto: 'Ricevuto',
    ...ticket_emails_js_1.TICKET_EMAIL_EVENT_TYPE_LABELS,
    allegato_ricevuto: 'Allegato ricevuto',
    allegato_rifiutato: 'Allegato rifiutato',
    allegato_eliminato: 'Allegato eliminato per retention',
    priorita_cambiata: 'Priorità cambiata',
    ...ticket_transitions_js_1.TICKET_STATE_EVENT_TYPE_LABELS,
    ...ticket_assignment_js_1.TICKET_ASSIGNMENT_EVENT_TYPE_LABELS,
    ...ticket_closure_js_1.TICKET_CLOSURE_EVENT_TYPE_LABELS,
    ...inbound_mail_js_1.TICKET_INBOUND_EVENT_TYPE_LABELS,
    ...ticket_emails_js_1.TICKET_RECEIPT_EVENT_TYPE_LABELS,
    ...reply_drafts_js_1.TICKET_REPLY_DRAFT_EVENT_TYPE_LABELS,
};
/** Header della chiave di idempotenza (DEC-009): obbligatorio su ogni creazione. */
exports.IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';
/** Header `true` sulla risposta di un replay (DEC-009). */
exports.IDEMPOTENT_REPLAYED_HEADER = 'Idempotent-Replayed';
/** 1-128 caratteri ASCII visibili (un UUID per invio, generato dal consumer). */
exports.IDEMPOTENCY_KEY_PATTERN = /^[\x21-\x7e]{1,128}$/;
/** `{PREFISSO}-{AAAA}-{NNNN}` (DEC-003): quattro cifre con zeri iniziali, di più oltre 9999. */
exports.TICKET_CODE_PATTERN = /^[A-Z]{2,5}-\d{4}-\d{4,}$/;
function formatTicketCode(prefix, year, sequence) {
    return `${prefix}-${year}-${String(sequence).padStart(4, '0')}`;
}
/** Testo obbligatorio, senza spazi ai bordi. */
const required = (label, max) => zod_1.z
    .string({ error: `${label}: campo obbligatorio` })
    .trim()
    .min(1, { error: `${label}: campo obbligatorio` })
    .max(max, { error: `${label}: troppo lungo (massimo ${max} caratteri)` });
/** Testo facoltativo: se presente, non vuoto. */
const optional = (label, max) => required(label, max).optional();
const email = (label) => zod_1.z
    .string({ error: `${label}: campo obbligatorio` })
    .trim()
    .toLowerCase()
    .pipe(zod_1.z.email({ error: `${label}: indirizzo email non valido` }));
/** Forme di identificativo che in una route parametrizzata non compaiono mai in chiaro. */
const IDENTIFIERS_IN_ROUTE_SEGMENT = [
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, // UUID
    /[0-9a-f]{32}/i, // UUID o hash senza trattini
    /\d{6,}/, // id numerici lunghi
];
/**
 * Vero se un segmento della route (path, query o frammento; i parametri `:id` esclusi) contiene un
 * identificativo in chiaro: UUID, esadecimale da 32 cifre, sei o più cifre di fila, codice fiscale
 * (contratto §5.2, easycare DEC-054: la route arriva parametrizzata).
 */
function routeHasIdentifier(route) {
    return route
        .split(/[/?&=#;]/)
        .filter((segment) => segment !== '' && !segment.startsWith(':'))
        .some((segment) => IDENTIFIERS_IN_ROUTE_SEGMENT.some((pattern) => pattern.test(segment)) || containsCodiceFiscale(segment));
}
/**
 * Prodotto e ambiente sono quelli del token (DEC-006 regola 2): il payload può ripeterli e, se
 * discordano, la richiesta è rifiutata. `prodotto` è il prefisso oppure l'id del prodotto.
 */
const declaredSource = {
    prodotto: optional('Prodotto', 80),
    ambiente: zod_1.z.enum(consumers_js_1.CONSUMER_ENVIRONMENTS, { error: 'Ambiente: valore non valido' }).optional(),
};
/** Come il nome di una categoria nella configurazione per prodotto (#18): al massimo 60 caratteri. */
const categoria = required('Categoria', 60);
const testo = required('Testo della segnalazione', exports.TICKET_TEXT_MAX);
/** Contesto tecnico comune ai due canali (contratto §5.1). */
const commonContext = {
    userAgent: required('Contesto, userAgent', 1000),
    browser: optional('Contesto, browser', 120),
    sistemaOperativo: optional('Contesto, sistemaOperativo', 120),
    versioneApplicativa: required('Contesto, versioneApplicativa', 64),
    correlationId: optional('Contesto, correlationId', 128),
};
/** Corpo di `POST /tickets` (canale autenticato, contratto §5.1 e §5.2). */
exports.createTicketSchema = zod_1.z.object({
    ...declaredSource,
    committente: required('Committente', 120),
    categoria,
    testo,
    segnalante: zod_1.z.object({
        /** Identificativo stabile risolto dall'API del prodotto, mai dal browser (easycare DEC-054). */
        id: required('Segnalante, id', exports.REPORTER_ID_MAX),
        email: email('Segnalante, email'),
        ruolo: required('Segnalante, ruolo', 80),
        unitaOperativa: required('Segnalante, unitaOperativa', 200),
    }, { error: 'Segnalante: campo obbligatorio' }),
    contesto: zod_1.z.object({
        etichettaSchermata: required('Contesto, etichettaSchermata', 200),
        /** Parametrizzata (`/cca/pazienti/:id`): un UUID in chiaro è rifiutato (contratto §5.2). */
        routePattern: required('Contesto, routePattern', 500).refine((value) => !routeHasIdentifier(value), {
            error: 'Contesto, routePattern: deve essere parametrizzata, senza identificativi in chiaro',
        }),
        /**
         * Solo l'identificativo interno, quando la regola del committente lo consente (#18). Resta per
         * compatibilità con EasyCare; gli altri prodotti usano `riferimentoRecord` (DEC-014).
         */
        identificativoPaziente: optional('Contesto, identificativoPaziente', exports.RECORD_REFERENCE_ID_MAX),
        /**
         * Il record su cui l'utente lavorava (contratto 1.4.0, DEC-014): `tipo` è una chiave dei tipi
         * di riferimento del prodotto (`recordReferenceTypes` della configurazione), `id`
         * l'identificativo interno, mai un codice fiscale. Conservato solo se la regola del
         * committente lo consente, come `identificativoPaziente`.
         */
        riferimentoRecord: zod_1.z
            .object({
            tipo: required('Contesto, riferimentoRecord.tipo', product_config_js_1.RECORD_REFERENCE_TYPE_KEY_MAX_LENGTH),
            id: required('Contesto, riferimentoRecord.id', exports.RECORD_REFERENCE_ID_MAX),
        }, { error: 'Contesto, riferimentoRecord: deve essere un oggetto con tipo e id' })
            .optional(),
        ...commonContext,
    }, { error: 'Contesto: campo obbligatorio' }),
});
/** Corpo di `POST /tickets/pre-accesso` (contratto §5.1 e §5.3): recapiti dichiarati e non verificati. */
exports.createPreAccessoTicketSchema = zod_1.z.object({
    ...declaredSource,
    categoria,
    testo,
    recapiti: zod_1.z.object({
        email: email('Recapiti, email'),
        telefono: optional('Recapiti, telefono', 40),
        struttura: optional('Recapiti, struttura', 200),
    }, { error: 'Recapiti: campo obbligatorio' }),
    contesto: zod_1.z.object(commonContext, { error: 'Contesto: campo obbligatorio' }),
});
/**
 * Campi che sul pre-accesso non esistono per costruzione (contratto §5.3): se presenti la
 * richiesta è rifiutata, non ripulita, così un consumer configurato male se ne accorge.
 */
exports.PRE_ACCESSO_FORBIDDEN_FIELDS = [
    'committente',
    'segnalante',
    'schermata',
    'log',
    'allegati',
    'contesto.etichettaSchermata',
    'contesto.routePattern',
    'contesto.identificativoPaziente',
    'contesto.riferimentoRecord',
];
/** I campi vietati presenti nel corpo grezzo (prima della validazione), nell'ordine dell'elenco. */
function forbiddenPreAccessoFields(body) {
    if (typeof body !== 'object' || body === null)
        return [];
    return exports.PRE_ACCESSO_FORBIDDEN_FIELDS.filter((field) => {
        const value = field.split('.').reduce((current, key) => {
            if (typeof current !== 'object' || current === null)
                return undefined;
            return current[key];
        }, body);
        return value !== undefined;
    });
}
/**
 * Codice fiscale italiano (16 caratteri, anche omocodico), riconosciuto anche dentro un testo più
 * lungo purché non attaccato ad altre lettere o cifre.
 */
const CODICE_FISCALE = '[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]';
exports.CODICE_FISCALE_PATTERN = new RegExp(`(?:^|[^A-Z0-9])${CODICE_FISCALE}(?:$|[^A-Z0-9])`, 'i');
/** Vero se il valore contiene qualcosa con la forma di un codice fiscale italiano. */
function containsCodiceFiscale(value) {
    return exports.CODICE_FISCALE_PATTERN.test(value);
}
/**
 * Chiavi di dati personali che non viaggiano mai come campo strutturato (contratto §5.2: nome,
 * cognome e codice fiscale di paziente e operatore) e un eventuale paziente strutturato. Confronto
 * sulla chiave normalizzata: minuscole, senza separatori (`codice_fiscale`, `codiceFiscale`, `CF`).
 */
exports.PERSONAL_DATA_KEYS = [
    'nome',
    'cognome',
    'nomecognome',
    'cognomenome',
    'nomecompleto',
    'codicefiscale',
    'cf',
    'firstname',
    'lastname',
    'fullname',
    'name',
    'surname',
    'givenname',
    'familyname',
    'fiscalcode',
    'taxcode',
    'paziente',
    'patient',
    'datipaziente',
    'anagrafica',
    'anagraficapaziente',
];
const PERSONAL_DATA_KEY_SET = new Set(exports.PERSONAL_DATA_KEYS);
const normalizeKey = (key) => key.toLowerCase().replace(/[^a-z0-9]/g, '');
/**
 * I percorsi del corpo grezzo, a qualunque livello, la cui chiave è un dato personale vietato
 * (es. `segnalante.nome`, `contesto.paziente`, `contesto.extra.0.codice_fiscale`).
 */
function forbiddenPersonalDataFields(body) {
    const found = [];
    const visit = (value, path) => {
        if (typeof value !== 'object' || value === null)
            return;
        for (const [key, child] of Object.entries(value)) {
            const childPath = [...path, key];
            if (!Array.isArray(value) && PERSONAL_DATA_KEY_SET.has(normalizeKey(key))) {
                found.push(childPath.join('.'));
                continue;
            }
            visit(child, childPath);
        }
    };
    visit(body, []);
    return found;
}
/**
 * Ruoli e unità del segnalante che indicherebbero un paziente: il canale paziente non esiste
 * (DEC-005), né come ruolo né come «Area Paziente».
 */
function mentionsPaziente(value) {
    return /paziente/i.test(value);
}
/** Codici di errore della ricezione dei ticket, nel campo `code` del corpo di errore dell'API. */
exports.TICKET_ERROR_CODES = {
    /** Header `Idempotency-Key` assente (DEC-009). */
    idempotencyKeyRequired: 'tickets.idempotency_key_required',
    /** Header `Idempotency-Key` fuori dal formato ammesso (DEC-009). */
    idempotencyKeyInvalid: 'tickets.idempotency_key_invalid',
    /** Stessa chiave di un invio precedente con un corpo diverso (DEC-009). */
    idempotencyKeyReused: 'tickets.idempotency_key_reused',
    /** Committente non fra quelli autorizzati del consumer (DEC-006 regola 3; codice fissato dal contratto §4). */
    committenteNotAuthorized: 'committente_non_autorizzato',
    /** `prodotto` nel corpo diverso dal prodotto del token (DEC-006 regola 2). */
    productMismatch: 'tickets.prodotto_discordante',
    /** `ambiente` nel corpo diverso dall'ambiente del token (DEC-006 regola 2). */
    environmentMismatch: 'tickets.ambiente_discordante',
    /** Categoria non fra le voci attive del prodotto per il canale (DEC-003, #18). */
    categoryNotAllowed: 'tickets.categoria_non_ammessa',
    /** `contesto.riferimentoRecord.tipo` non fra i tipi di riferimento del prodotto (DEC-014). */
    recordReferenceTypeNotAllowed: 'tickets.tipo_riferimento_non_ammesso',
    /** Campo che sul canale pre-accesso non esiste (contratto §5.3). */
    fieldNotAllowedPreAccesso: 'tickets.campo_non_ammesso_pre_accesso',
    /** Nome, cognome, codice fiscale o paziente come campo strutturato (contratto §5.2). */
    personalDataNotAllowed: 'tickets.dato_personale_non_ammesso',
    /** Ruolo o unità del segnalante che indicano un paziente: canale inesistente (DEC-005). */
    patientReporterNotAllowed: 'tickets.segnalante_paziente_non_ammesso',
    /** Troppe richieste del consumer nel minuto (contratto §11). */
    rateLimited: 'tickets.rate_limited',
};
//# sourceMappingURL=tickets.js.map